import { z } from 'zod';

import {
  hostnameSchema,
  ipv4Schema,
  isoDateTimeSchema,
  labelsSchema,
  positiveIntSchema,
  semverSchema,
  slugSchema,
  uuidSchema,
} from '../common/primitives';

import { nodeAllocationSchema, nodeCapacitySchema } from './capacity';
import { nodeMetricsSchema } from './metrics';
import { nodeStatusSchema } from './node-status';
import { preflightReportSchema } from './preflight';

/**
 * Lesesicht auf einen Node, wie sie die Administrations-API und später das
 * Vue-Frontend erhalten.
 *
 * Bewusst ein eigener Vertrag und keine direkte Serialisierung der
 * TypeORM-Entität: Die Entität enthält Token-Hashes und interne Felder, die
 * niemals über die API gehen dürfen. Eine eigene Sicht macht diese Trennung
 * zur Pflicht statt zur Sorgfaltsfrage.
 */

export const nodeViewSchema = z.object({
  id: uuidSchema,
  /**
   * `null`, solange sich der Server noch nicht registriert hat.
   *
   * Seit M4b tippt niemand mehr einen Hostnamen ein: Der Master legt eine Zeile
   * an, das Bootstrap-Skript ermittelt Namen und Adressen auf dem Server und
   * meldet sie bei der Registrierung. Zwischen beiden Schritten gibt es diese
   * Zeile ohne Namen — und sie als leere Zeichenkette auszugeben wäre eine
   * Behauptung über etwas, das noch niemand beobachtet hat.
   */
  hostname: hostnameSchema.nullable(),
  /** Freie Notiz des Betreibers, damit ein Node vor der Registrierung unterscheidbar ist. */
  note: z.string().max(200).nullable(),
  publicIpv4: ipv4Schema.nullable(),
  privateIpv4: ipv4Schema.nullable(),
  agentPort: positiveIntSchema.nullable(),
  ingressPort: positiveIntSchema.nullable(),
  /**
   * Die laufende Nummer dieses Workers in der Flotte, vergeben beim Anlegen.
   *
   * Sie entscheidet, über welchen Einstiegspunkt der Edge die Kunden dieses
   * Workers ihre Dateien hochladen — `sftp<ordinal>` auf
   * `SFTP_BASE_PORT + ordinal - 1`. Lücken werden nicht aufgefüllt: Ein neuer
   * Node an der Stelle eines abgebauten erbte sonst dessen Port, und die
   * Zugangsdaten alter Kunden zeigten auf einen fremden Server.
   */
  ordinal: positiveIntSchema,
  /** Der Port, den der Kunde in seinem SFTP-Programm einträgt. Abgeleitet aus `ordinal`. */
  sftpPort: positiveIntSchema,
  region: slugSchema,
  labels: labelsSchema,
  agentVersion: semverSchema.nullable(),
  status: nodeStatusSchema,
  /** Administrative Freigabe für den Scheduler, unabhängig vom Status. */
  schedulable: z.boolean(),
  generation: positiveIntSchema.nullable(),
  capacity: nodeCapacitySchema.nullable(),
  allocated: nodeAllocationSchema,
  /** Messwerte des letzten Heartbeats. `null`, solange keiner eingegangen ist. */
  lastMetrics: nodeMetricsSchema.nullable(),
  lastPreflight: preflightReportSchema.nullable(),
  lastHeartbeatAt: isoDateTimeSchema.nullable(),
  /** Alter des letzten Heartbeats in Millisekunden — erspart dem Client die Rechnung. */
  heartbeatAgeMs: z.number().nonnegative().nullable(),
  enrolledAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export type NodeView = z.infer<typeof nodeViewSchema>;

/** Kompakte Zusammenfassung der gesamten Flotte für Dashboard und Alarmierung. */
export const fleetSummarySchema = z.object({
  totalNodes: z.number().int().nonnegative(),
  byStatus: z.record(nodeStatusSchema, z.number().int().nonnegative()),
  /** Nodes, denen der Scheduler aktuell Sites zuweisen darf. */
  schedulableNodes: z.number().int().nonnegative(),
  totalCapacity: nodeCapacitySchema,
  totalAllocated: nodeAllocationSchema,
  generatedAt: isoDateTimeSchema,
});

export type FleetSummary = z.infer<typeof fleetSummarySchema>;
